import { useCallback, useEffect, useRef } from 'react';
import { isReloadSafe, reloadBlockReason } from '../services/reloadGuard'; // [AUTO-REFRESH 2026-08-03]
import { captureUiSnapshot, setSnapshotUser } from '../services/uiSnapshot'; // [SEAMLESS-UPDATE 2026-08-13]
import { useAuth } from '../contexts/AuthContext'; // [SEAMLESS-UPDATE 2026-08-13] scopes the snapshot

/*
 * [VERSION-BANNER 2026-07-07 → FORCED-UPDATE 07-21 → SOFT-NOTICE 07-23 → AUTO-REFRESH 08-03
 *  → SEAMLESS-UPDATE 2026-08-13]
 * Deploy → every open tab takes the new build on its own. Nobody is asked to do anything.
 *
 * The app is a static SPA served by nginx: a tab keeps running the bundle it downloaded, so a
 * deploy does not reach it until the document reloads. Nothing supported can swap a running SPA's
 * code underneath it — not a service worker, not module federation. So the goal was never to avoid
 * the reload. It is to reload at a moment nobody notices AND to put people back where they were.
 * (Native apps people call seamless work the same way: the new bundle is applied on next launch,
 * not hot-swapped into a live screen.)
 *
 * THE SEQUENCE, and every step exists because of a specific way this breaks without it:
 *
 *   1. POLL          /version.json every 60s, plus whenever the tab regains focus. Each tab's
 *                    clock starts at ITS load, which is also most of the herd-spreading for free.
 *   2. SETTLE        Hold a newly-seen id for SETTLE_MS before acting. The deploy rewrites build/
 *                    IN PLACE, so a fresh version.json can advertise a build whose chunks are
 *                    still being written.
 *   3. PREFLIGHT     Fetch the new index.html and its hashed assets and require them to serve.
 *                    Turns the timer into a real check — and warms the browser cache, which is
 *                    what makes the reload that follows near-instant with no service worker.
 *   4. SAME-BUNDLE   If the served bundle is the one already running, this was a SERVER-ONLY
 *                    deploy. Adopt the id and stop. Nobody reloads for a byte-identical page.
 *   5. IDLE GAP      Wait for IDLE_GAP_MS of no input. Safe (step 6) is not the same as polite;
 *                    reloading under someone reading a table is the jolt this exists to avoid.
 *   6. RELOAD GUARD  Never over an in-flight write, an open dialog, or a focused field.
 *   7. JITTER        A per-tab random offset, so tabs that qualify together do not reload together.
 *   8. SNAPSHOT      Capture filters (services/uiSnapshot) immediately before reloading, and
 *                    restore them as the next load's INITIAL state — so there is no flash of the
 *                    default month and no second fetch.
 *   9. LOOP GUARD    Remember the build we reloaded towards; if we come back still on the old one,
 *                    stop and show the pill rather than loop.
 *
 * ⚠ [NO-PILL 2026-08-24] THERE IS NO PILL ANY MORE, AND NOTHING IS EVER SHOWN TO THE USER.
 * It survived until now as the failure path — a stuck preflight, a reload that did not take, a
 * dialog that never closed. The user asked for it to go, and the fact they were seeing it at all
 * meant they were hitting those paths regularly. The commonest by far is the least dramatic:
 * leaving a search or filter box focused, which isReloadSafe() correctly reads as "typing".
 *
 * ⚠ DELETING THE PILL ALONE WOULD HAVE BEEN A BUG. Showing it also set shownRef, and every later
 * check() returned early on that flag — so one appearance meant the tab never auto-updated again
 * for the rest of its life, even after the modal closed. That is why each failure path below now
 * DEFERS AND RETRIES (see deferRetry) instead of giving up. The 30s poll and the tab-return
 * handler come round again, and the first moment the tab is free, the update lands.
 *
 * ⚠ AND A RETURN TO THE TAB IS TAKEN IMMEDIATELY. Someone who has just come back has not started
 * working yet, so the idle gap and the herd jitter — both there to avoid interrupting a person
 * mid-thought — have nothing to protect and are skipped for that one attempt. The safety guards
 * (settle, preflight, isReloadSafe) are NOT skipped, ever.
 *
 * ⚠ AND THE 2026-07-21 FAILURE MUST NOT BE REPEATED. That build reloaded the instant version.json
 * changed, landed mid-deploy and served nginx 500s. Steps 2 and 3 are precisely what make this
 * safe; neither may be removed to make updates arrive faster.
 *
 * Data already streams in live via the 4s data-version poll — this is only for CODE/UI changes.
 * Dormant in dev (no REACT_APP_BUILD).
 */
const VERSION_URL = '/version.json';
/**
 * [SETTLE-PARITY 2026-08-13] 30s, matching the PM board's VERSION_POLL_MS, so both surfaces
 * NOTICE a deploy on the same clock and the shared 3-minute SETTLE_MS starts at the same moment
 * on each. At 60s the portal could sit up to a minute behind the board before its hold even
 * began, which is why the same deploy appeared to take longer here.
 *
 * This is detection granularity ONLY — it is not what decides when a build is taken (SETTLE_MS
 * is). Cost is one extra conditional GET of a small static file per tab per minute, served by
 * nginx with no DB involvement.
 */
const POLL_MS = 30_000;
/**
 * [SEAMLESS-UPDATE 2026-08-13] How long a person must be doing nothing before we take the tab.
 * Short enough that a natural pause — finishing a sentence, looking at a figure, reaching for a
 * coffee — is enough; long enough that it never lands in the middle of reading a row.
 */
const IDLE_GAP_MS = 20_000;
/** Upper bound of the per-tab random offset. See the note where it is applied. */
const JITTER_MAX_MS = 45_000;
// [SETTLE-DELAY 2026-07-30] How long to wait after FIRST seeing a new build id before acting on
// it. [SETTLE-PARITY 2026-08-13] Deliberately the SAME 3 minutes as the PM board's SETTLE_MS
// (PMSingaporePage) — one deploy, one hold, both surfaces. Change them together or they drift.
//
// ⚠ 3 minutes is the HOLD, not the wall-clock. Detection adds up to POLL_MS (30s), then this
// hold runs, then JITTER_MAX_MS and IDLE_GAP_MS still apply here — so the portal lands at
// roughly 3.5-4 min for an idle tab, and later for someone typing continuously (by design; see
// the no-hard-cap note at the idle-gap check). The board has no idle gap because nobody types
// at a wall-mounted TV.
const SETTLE_MS = 3 * 60_000;
/**
 * [NO-PILL 2026-08-24] How long a build that we reloaded towards — and did not end up running
 * — is left alone before one more attempt is allowed. Mirrors the PM Singapore board's guard
 * of the same name and for the same reason: a half-served deploy must be able to stall a tab,
 * but never to strand it permanently now that there is no notice to fall back on.
 */
const RELOAD_RETRY_MS = 5 * 60_000;

// Baseline = the id BAKED INTO THIS BUNDLE at compile time (REACT_APP_BUILD, injected by
// scripts/stamp-version.js). It is the id this tab is ACTUALLY running, so even if a CDN
// serves a stale bundle alongside a fresh /version.json the mismatch is still caught.
// Unset in dev (no production build) → the whole component stays dormant.
const BASELINE: string | null = process.env.REACT_APP_BUILD || null;

async function fetchBuild(): Promise<string | null> {
  try {
    // Cache-bust + no-store so a proxy/browser cache can't hide a just-deployed version.json.
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.build === 'string' ? data.build : null;
  } catch {
    return null;
  }
}

/* ── [AUTO-REFRESH 2026-08-03] ── */

// sessionStorage key holding the build id we last reloaded TOWARDS. Per-tab (not localStorage)
// so one tab's failed reload never suppresses another's.
const AUTO_RELOAD_KEY = 'ub-auto-reload-to';

/** "Is somebody actually looking at this tab right now?" Visible AND focused. A hidden tab is
 *  obviously unattended; a visible-but-unfocused one means they are in another window, which
 *  is exactly the "came back to the app" case we want to reload on. */
function isTabActive(): boolean {
  try {
    if (document.hidden) return false;
    // hasFocus is unsupported nowhere that matters, but treat a throw as "active" so we fail
    // SAFE — showing the pill is always an acceptable outcome, an unwanted reload is not.
    return document.hasFocus();
  } catch {
    return true;
  }
}

/**
 * Confirm the new build is fully servable BEFORE reloading into it.
 *
 * `npm run build` rewrites the live build/ directory in place, so during a deploy
 * /version.json can already advertise the new id while chunks are still being written —
 * reloading then lands on a chunk-load error. We fetch the new index.html, pull out the
 * asset URLs it references, and require each to come back OK with a non-trivial body.
 *
 * Returns false on ANY doubt: a failed preflight simply means "not yet", and we try again on
 * the next poll. It is never a reason to reload anyway.
 */
/**
 * [SEAMLESS-UPDATE 2026-08-13] The hashed bundle THIS tab is running.
 *
 * Used to answer a question the build id cannot: did the client actually change? version.json gets
 * a fresh id on every deploy, including the many that only touch server code — so a server-only
 * release was reloading every open tab for a bundle byte-identical to the one already running.
 * Asset names are content-hashed, so comparing them answers it exactly.
 */
function currentMainScript(): string | null {
  try {
    const el = document.querySelector('script[src*="/static/js/"]') as HTMLScriptElement | null;
    if (!el) return null;
    return new URL(el.src, window.location.origin).pathname;
  } catch {
    return null;
  }
}

/**
 * `ok` — the new build verifiably serves. `sameBundle` — it is byte-for-byte the bundle already
 * running, i.e. a server-only deploy with nothing for this tab to load.
 *
 * ⚠ RETURNED, not stashed in a module variable: the manual Refresh button and the automatic path
 * can both be preflighting at once, and a shared flag would let one clobber the other's answer.
 */
interface Preflight { ok: boolean; sameBundle: boolean }

async function preflightNewBuild(): Promise<Preflight> {
  let sameBundle = false;
  try {
    const htmlRes = await fetch(`/index.html?t=${Date.now()}`, { cache: 'no-store' });
    if (!htmlRes.ok) return { ok: false, sameBundle: false };
    const html = await htmlRes.text();
    // index.html is tiny; if it came back suspiciously short it is being rewritten right now.
    if (html.length < 200) return { ok: false, sameBundle: false };

    const assets = [
      ...Array.from(html.matchAll(/<script[^>]+src="([^"]+)"/g)).map((m) => m[1]),
      ...Array.from(html.matchAll(/<link[^>]+href="([^"]+\.css)"/g)).map((m) => m[1]),
    ].filter((u) => u.includes('/static/'));

    // No hashed assets referenced at all → not an index.html we understand; don't gamble.
    if (!assets.length) return { ok: false, sameBundle: false };

    /* [SEAMLESS-UPDATE 2026-08-13] Same bundle → nothing for this tab to load. Recorded so the
       caller can drop the whole thing instead of reloading into an identical page. Compared on
       pathname so a cache-busting query string can never make identical bundles look different. */
    const mine = currentMainScript();
    if (mine) {
      const theirs = assets
        .map((u) => { try { return new URL(u, window.location.origin).pathname; } catch { return u; } })
        .filter((p) => p.includes('/static/js/'));
      if (theirs.length && theirs.includes(mine)) sameBundle = true;
    }

    // Asset URLs are content-hashed, so the DEFAULT cache is correct here (not no-store):
    // a hit warms the browser cache and makes the reload that follows near-instant.
    for (const url of assets) {
      const res = await fetch(url);
      if (!res.ok) return { ok: false, sameBundle: false };
      const body = await res.text();
      // A partially-written file can still answer 200 with a truncated body — length is a
      // crude but effective check against exactly that.
      if (body.length < 500) return { ok: false, sameBundle: false };
    }
    return { ok: true, sameBundle };
  } catch {
    return { ok: false, sameBundle: false };
  }
}

/* [NO-PILL 2026-08-24] The pill's styled components (Bar / Label / RefreshBtn and their two
   keyframes) lived here. They are gone along with the notice itself — this component now
   renders nothing at all and exists purely for its effects. */

const UpdateBanner: React.FC = () => {
  // [SETTLE-DELAY 2026-07-30] The new build id we have SEEN but are still holding back, and
  // when we first saw it. Refs, not state, so the settle window survives re-renders without
  // re-running the poll effect.
  const pendingRef = useRef<{ build: string; firstSeenAt: number } | null>(null);
  const timerRef = useRef<number | null>(null);

  // [AUTO-REFRESH 2026-08-03] Build id we are waiting to auto-reload into once the tab comes
  // back. Set only when the settle window closes on an INACTIVE tab.
  const armedRef = useRef<string | null>(null);
  // Guards against a stuck loop of preflight attempts on one return.
  const attemptingRef = useRef(false);
  const failuresRef = useRef(0);

  /* [SEAMLESS-UPDATE 2026-08-13] Last real input from this person, and when this tab armed.
     Together with jitterRef they are the whole "go at a moment nobody notices" decision. */
  const lastInputAtRef = useRef(Date.now());
  const armedAtRef = useRef(0);
  /** Chosen ONCE per tab, so tabs that qualify together still go at different moments. */
  const jitterRef = useRef(Math.floor(Math.random() * JITTER_MAX_MS));
  /**
   * [NO-PILL 2026-08-24] Set by the visibility/focus handler, cleared by the attempt it triggers.
   * It marks the ONE attempt that follows a person coming back to the tab, which is allowed to
   * skip the politeness delays — they have not started working yet, so there is nothing to
   * interrupt. It never skips a safety check.
   */
  const justReturnedRef = useRef(false);
  /** Who to scope the restored filters to. Kept in a ref so the reload path never re-renders. */
  const userIdRef = useRef<number | null>(null);
  const { user } = useAuth();
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
    setSnapshotUser(user?.id ?? null);
  }, [user]);

  /**
   * Real input only. `mousemove` is included on purpose — somebody reading a table and drifting the
   * cursor across it is working, and reloading under them is the jolt this exists to avoid. All
   * passive, all on window, so nothing here can cost a frame.
   */
  useEffect(() => {
    const mark = () => { lastInputAtRef.current = Date.now(); };
    const evts: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];
    evts.forEach((e) => window.addEventListener(e, mark, { passive: true, capture: true }));
    return () => evts.forEach((e) => window.removeEventListener(e, mark, { capture: true } as EventListenerOptions));
  }, []);
  /**
   * [AUTO-REFRESH 2026-08-03] Last moment we OBSERVED this tab being active.
   *
   * This exists because the active/inactive decision cannot simply be "is the tab active
   * right now?" at the moment we get around to evaluating it. Browsers throttle timers and
   * intervals in hidden tabs, so a tab that was away for the whole settle window typically
   * evaluates it on RETURN — at which point the tab is active again, and a naive check would
   * show the pill: exactly the case this feature exists to remove.
   *
   * So we record when the tab was last seen active and compare that against when the settle
   * window actually CLOSED. Accurate to one poll interval, which is all this needs.
   */
  const lastActiveAtRef = useRef(Date.now());
  const markActive = useCallback(() => {
    if (isTabActive()) lastActiveAtRef.current = Date.now();
  }, []);

  /**
   * [NO-PILL 2026-08-24] A reason not to reload RIGHT NOW — never a reason to stop trying.
   *
   * Replaces fallbackToPill, which cleared armedRef and showed the notice. Every caller is a
   * TEMPORARY condition: an open dialog gets closed, a focused field gets blurred, a half-written
   * deploy finishes. armedRef is deliberately left set, so the next poll or the next return to
   * the tab simply tries again. Console only — the user is never asked to do anything.
   */
  const deferRetry = useCallback((why: string) => {
    attemptingRef.current = false;
    console.info(`[update] holding the reload for now (${why}) — will retry`);
  }, []);

  /**
   * The tab just became active while an update was armed. Reload into the new build if — and
   * only if — it is safe and the build is actually complete. Anything else degrades to the
   * pill rather than reloading on a guess.
   */
  const tryAutoReload = useCallback(async () => {
    const build = armedRef.current;
    if (!build || attemptingRef.current) return;
    if (!isTabActive()) return; // not back yet — stay armed

    /**
     * [SEAMLESS-UPDATE 2026-08-13] WAIT FOR A GAP. This is what makes the reload unnoticeable to
     * somebody who is actually working: not "are they allowed to be interrupted" (isReloadSafe
     * answers that) but "are they mid-thought right now".
     *
     * A person reading a table, moving the mouse, scrolling — reloading under them is exactly the
     * jolt this feature exists to avoid, even though it would be perfectly SAFE. Waiting for a few
     * seconds of no input costs nothing: the poll comes round again, and the first real pause takes
     * it. In practice everybody pauses within a minute or two, and switching tabs or apps triggers
     * the return path immediately anyway.
     *
     * ⚠ There is deliberately NO hard cap forcing a reload on a still-busy person. A cap would put
     * the jolt back for exactly the person who was most engaged. Someone genuinely never pausing
     * simply stays on the old bundle a little longer, which is strictly better than today, where
     * they would be ignoring a pill instead.
     */
    /* [NO-PILL 2026-08-24] `returning` = this attempt was triggered by the tab becoming visible
       or regaining focus. Consumed here so it applies to exactly one attempt. */
    const returning = justReturnedRef.current;
    justReturnedRef.current = false;

    if (!returning && Date.now() - lastInputAtRef.current < IDLE_GAP_MS) return; // still working — try again later

    /**
     * ⚠ AND SPREAD THE HERD. Every tab that qualifies at the same moment would otherwise reload in
     * the same second, and each reload boots the app and fans out to several API calls against an
     * RDS this app shares with three others.
     *
     * Most of the spreading is already free — each tab's poll clock starts when that tab loaded, so
     * detection is naturally staggered across POLL_MS. The exception is everyone returning to the
     * app at once (a shift starting, a meeting ending), which fires the focus path in lockstep.
     * A per-tab offset chosen ONCE at load covers that case for the price of one line, and is
     * deliberately preferred over anything that makes tabs coordinate with each other or the
     * server — that would be far more machinery than a burst this size warrants (176 concurrent
     * requests from 48 real users was measured at zero failures).
     */
    if (!returning && Date.now() - armedAtRef.current < jitterRef.current) return;

    // Never reload over unsaved work / an in-flight write. They came back to a modal or a
    // running save, so hand them the pill and let them choose the moment.
    if (!isReloadSafe()) {
      deferRetry(reloadBlockReason() || 'unsafe');
      return;
    }

    /* Loop guard: if we already reloaded towards this exact build and are STILL not running it,
       reloading again would just repeat.

       ⚠ [NO-PILL 2026-08-24] IT NOW EXPIRES. It used to blacklist that build id for the life of
       the tab and hand the user the pill; with no pill, a permanent block would mean a tab stuck
       on the old bundle for ever with nobody told. So it backs off for RELOAD_RETRY_MS and then
       allows one more attempt — at worst one reload per five minutes, and the tab always heals
       in the end. This is exactly the fix the PM Singapore board took on 2026-08-12, for the
       same reason: a half-served deploy must not be able to strand a screen permanently. */
    try {
      const [guardBuild, guardAt] = (sessionStorage.getItem(AUTO_RELOAD_KEY) || '').split('|');
      if (guardBuild === build && Date.now() - Number(guardAt || 0) < RELOAD_RETRY_MS) {
        deferRetry('a previous auto-reload did not take — backing off');
        return;
      }
    } catch { /* sessionStorage unavailable → skip the guard; preflight still gates the reload */ }

    attemptingRef.current = true;
    const pre = await preflightNewBuild();
    attemptingRef.current = false;

    /* [SEAMLESS-UPDATE 2026-08-13] A server-only deploy: new build id, identical bundle. There is
       nothing for this tab to load, so adopt the id and carry on — no reload, no pill, nobody
       interrupted. Without this every server release refreshed every open tab for no reason. */
    if (pre.ok && pre.sameBundle) {
      armedRef.current = null;
      pendingRef.current = null;
      failuresRef.current = 0;
      return;
    }

    if (!pre.ok) {
      /* Still mid-deploy, most likely. [NO-PILL 2026-08-24] There is no attempt cap any more:
         giving up after three tries used to mean the pill, and now it would mean silence. A
         build that never verifies simply never gets taken, which is the correct outcome —
         reloading into a bundle that does not serve is the one thing worse than not reloading.
         The count is kept purely so the console shows how long this has been going on. */
      failuresRef.current += 1;
      deferRetry(`the new build did not verify (attempt ${failuresRef.current})`);
      return;
    }

    try { sessionStorage.setItem(AUTO_RELOAD_KEY, `${build}|${Date.now()}`); } catch { /* best effort */ }
    /* [SEAMLESS-UPDATE 2026-08-13] Take the snapshot LAST — after every guard has passed and
       immediately before the reload — so a filter changed while we were preflighting is still
       captured, and so nothing is ever written for a reload that does not happen. */
    captureUiSnapshot(userIdRef.current);
    window.location.reload();
  }, [deferRetry]);

  // Clear the loop-guard marker once we are demonstrably running the build it points at —
  // otherwise the NEXT deploy that happens to reuse that id would be suppressed.
  useEffect(() => {
    try {
      const [guardBuild] = (sessionStorage.getItem(AUTO_RELOAD_KEY) || '').split('|');
      if (BASELINE && guardBuild === BASELINE) {
        sessionStorage.removeItem(AUTO_RELOAD_KEY);
      }
    } catch { /* ignore */ }
  }, []);

  /**
   * [AUTO-REFRESH 2026-08-03] The settle window has closed for `build` — decide what happens.
   * This is the single fork in the whole feature.
   *
   * The question is NOT "is the tab active now", it is "was anyone here when the 3 minutes
   * ran out". Those differ precisely in the case that matters: a tab that was away the whole
   * time only gets to run this code once it comes back (throttled timers), and "now" would
   * wrongly read as active. Comparing the last observed active moment against the settle-close
   * moment answers the real question in both paths.
   *
   * The one-poll-interval tolerance keeps a tab that was active THROUGHOUT on the pill path
   * even though we only sample its activity once a minute — e.g. someone who glanced at
   * another window for a few seconds mid-window is still "here" and must not be reloaded.
   */
  const onSettled = useCallback((build: string, firstSeenAt: number) => {
    if (armedRef.current) return; // already decided for this build
    /**
     * [SEAMLESS-UPDATE 2026-08-13] BOTH PATHS NOW END IN A RELOAD. The fork above used to be
     * "nobody here → reload on return; somebody here → hand them a pill". The pill is gone from
     * the normal flow: an update should reach a working person the same way it reaches an idle
     * tab, without asking them to do anything about it.
     *
     * What was "was anyone here" is now only about TIMING, not about outcome. Both cases arm;
     * tryAutoReload decides when it is polite to go — see the idle gate below. The pill survives
     * solely as the failure path (fallbackToPill), for a tab that cannot safely reload.
     *
     * ⚠ THE PREVIOUS ATTEMPT AT THIS FAILED, and it is worth knowing why this one is different.
     * A forced-update build on 2026-07-21 reloaded the instant version.json changed and landed
     * mid-deploy, serving nginx 500s; it was removed two days later. Both causes are now fixed
     * and must stay fixed: SETTLE_MS means we never act on a brand-new id, and preflightNewBuild
     * proves the bundle actually serves before we reload into it.
     */
    armedRef.current = build;
    armedAtRef.current = Date.now();
    void tryAutoReload();
  }, [tryAutoReload]);

  const check = useCallback(async () => {
    if (!BASELINE) return;   // dev / no baked build id → dormant
    const b = await fetchBuild();
    if (!b || b === BASELINE) return;

    const now = Date.now();
    const pending = pendingRef.current;

    // A DIFFERENT new id (a second deploy landed while we were waiting) restarts the clock,
    // so we always settle for the full window after the LATEST deploy.
    if (!pending || pending.build !== b) {
      pendingRef.current = { build: b, firstSeenAt: now };
      // [AUTO-REFRESH 2026-08-03] A newer deploy supersedes anything we had armed: re-settle
      // from scratch so the 3-minute window always applies to the LATEST build.
      armedRef.current = null;
      failuresRef.current = 0;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      // Fire exactly when the window closes, so the pill is not delayed by up to a further
      // POLL_MS waiting for the next poll to come round.
      timerRef.current = window.setTimeout(() => {
        const p = pendingRef.current;
        if (p && Date.now() - p.firstSeenAt >= SETTLE_MS) onSettled(p.build, p.firstSeenAt);
      }, SETTLE_MS);
      return;
    }

    // Same id still live — decide once the settle window has elapsed. Elapsed time is
    // computed from the stored timestamp rather than trusted to the timer, because browsers
    // throttle timers in background tabs; this way a hidden tab still settles on its
    // next poll or as soon as it regains focus.
    if (now - pending.firstSeenAt >= SETTLE_MS) onSettled(b, pending.firstSeenAt);
  }, [onSettled]);

  useEffect(() => {
    if (!BASELINE) return; // dormant in dev (REACT_APP_BUILD unset)
    check(); // check immediately too, so a stale-cached bundle is caught right on load

    const id = setInterval(() => {
      // [AUTO-REFRESH 2026-08-03] Sample activity BEFORE checking, so lastActiveAt reflects
      // this tick. This interval is what keeps the "was anyone here" record fresh.
      markActive();
      check();
      void tryAutoReload();
    }, POLL_MS);

    // [AUTO-REFRESH 2026-08-03] "Came back to the app" — either the tab became visible again
    // or the window regained focus. Both are the same moment for our purposes, and
    // tryAutoReload is idempotent + re-checks isTabActive, so firing on both is safe.
    //
    // NOTE: this handler must NOT sample activity. `check()` is async, so anything after it
    // runs BEFORE the settle decision inside it — stamping the tab active here would make a
    // tab that was away all along look present and send it down the pill path. The 60s poll
    // is the only place activity is recorded, which is all the resolution this needs.
    const onReturn = () => {
      if (document.hidden) return;
      // [NO-PILL 2026-08-24] Mark this as a RETURN so the attempt below may skip the idle gap and
      // the jitter. Set before check(), because check() can arm and synchronously attempt.
      justReturnedRef.current = true;
      check();              // catch a deploy that landed while they were away (may arm)
      void tryAutoReload(); // and reload straight into it if one is armed
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [check, tryAutoReload, markActive]);

  /* [NO-PILL 2026-08-24] Renders nothing, ever. This component is now purely the effects
     above: poll, settle, preflight, wait for a polite moment, snapshot the filters, reload.
     The manual Refresh button and its "Still deploying" state went with the pill — a person
     who wants the new build sooner just refreshes the browser, which is what the user asked
     for. Kept as a component (rather than folded into a hook) so <App/> is untouched. */
  return null;
};

export default UpdateBanner;
